import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../lib/api';
import { toast } from 'sonner';
import { ChartPieSlice, FilePdf, FileXls, CaretDown, CaretUp } from '@phosphor-icons/react';

const ReportBody = ({ reportKey }) => {
  const { data, isLoading } = useQuery({ queryKey: ['report', reportKey], queryFn: () => api.get(`/reports/${reportKey}`).then((r) => r.data) });
  if (isLoading || !data) return <p className="text-sm text-slate-400 py-3" data-testid="report-loading">Building report...</p>;
  return (
    <div className="grid md:grid-cols-2 gap-4 pt-3" data-testid={`report-body-${reportKey}`}>
      {data.sections.map((s) => (
        <div key={s.heading}>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">{s.heading}</p>
          <table className="w-full text-sm border border-slate-100 rounded">
            <thead className="bg-slate-50"><tr>{s.headers.map((h) => <th key={h} className="text-left py-1.5 px-2 font-mono-label text-[9px] uppercase text-slate-400">{h}</th>)}</tr></thead>
            <tbody>
              {s.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-50">{r.map((c, j) => <td key={j} className={`py-1.5 px-2 ${j > 0 ? 'font-medium' : ''}`}>{String(c)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default function Reports() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(null);
  const { data: catalog, isLoading } = useQuery({ queryKey: ['reports-catalog'], queryFn: () => api.get('/reports').then((r) => r.data) });
  const schedMut = useMutation({
    mutationFn: ({ key, frequency }) => api.post(`/reports/${key}/schedule`, { frequency }),
    onSuccess: (r) => {
      toast.success(r.data.frequency === 'off' ? 'Schedule removed' : `Report scheduled ${r.data.frequency} - emailed to all principals and school admins`);
      qc.invalidateQueries({ queryKey: ['reports-catalog'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="reports-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1 flex items-center gap-2"><ChartPieSlice size={30} className="text-orange-600" />Reports Center</h1>
      <p className="text-sm text-slate-500 mb-6">Cross-module leadership reports - view live, export PDF or Excel, or schedule email delivery to principals and school admins.</p>
      {isLoading && <p className="text-sm text-slate-400" data-testid="reports-loading">Loading catalog...</p>}
      <div className="space-y-3">
        {(catalog || []).map((r) => (
          <div key={r.key} className="bg-white border border-slate-200 rounded-xl p-4 card-hover" data-testid={`report-card-${r.key}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <button data-testid={`report-toggle-${r.key}`} onClick={() => setOpen(open === r.key ? null : r.key)}
                className="flex items-center gap-2 text-left flex-1 min-w-[200px]">
                {open === r.key ? <CaretUp size={14} className="text-orange-600" /> : <CaretDown size={14} className="text-slate-400" />}
                <span>
                  <span className="font-display font-bold block">{r.title}</span>
                  <span className="text-xs text-slate-500">{r.desc}</span>
                </span>
              </button>
              <a data-testid={`report-pdf-${r.key}`} href={`${API}/reports/${r.key}.pdf`}
                className="text-xs border border-slate-200 rounded px-2 py-1.5 hover:border-orange-500 flex items-center gap-1"><FilePdf size={13} className="text-rose-600" />PDF</a>
              <a data-testid={`report-xlsx-${r.key}`} href={`${API}/reports/${r.key}.xlsx`}
                className="text-xs border border-slate-200 rounded px-2 py-1.5 hover:border-orange-500 flex items-center gap-1"><FileXls size={13} className="text-green-700" />Excel</a>
              <select data-testid={`report-schedule-${r.key}`} value={r.schedule}
                onChange={(e) => schedMut.mutate({ key: r.key, frequency: e.target.value })}
                className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white">
                <option value="off">Not scheduled</option>
                <option value="weekly">Email weekly (Mon)</option>
                <option value="monthly">Email monthly (1st)</option>
              </select>
            </div>
            {open === r.key && <ReportBody reportKey={r.key} />}
          </div>
        ))}
      </div>
    </div>
  );
}
